"use client"

import React, { useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Header } from "../components/Header"
import { Bike, Package, Info, ArrowRight } from "lucide-react"
import { useServicePalette } from "../hooks/useServicePalette"
import { useAuthStore } from "../stores/useAuthStore"

export function OrderTypePage() {
  const { tableId } = useParams<{ tableId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const palette = useServicePalette()
  const { config } = useAuthStore()

  const handleSelect = (orderType: "delivery" | "pickup") => {
    navigate(`/order-confirmation/${tableId}`, {
      state: { ...location.state, orderType },
    })
  }

  useEffect(() => {
    if (!config) return

    const onlyDelivery = config.accept_delivery && !config.accept_takeaway
    const onlyTakeaway = config.accept_takeaway && !config.accept_delivery

    if (onlyDelivery) {
      navigate(`/order-confirmation/${tableId}`, {
        state: { ...location.state, orderType: "delivery" },
        replace: true,
      })
    } else if (onlyTakeaway) {
      navigate(`/order-confirmation/${tableId}`, {
        state: { ...location.state, orderType: "pickup" },
        replace: true,
      })
    }
  }, [config, navigate, tableId, location.state])

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24`}
    >
      <Header title="Tipo de Pedido" showBack />

      <div className="px-4 py-8 space-y-8 max-w-md mx-auto">
        {/* Hero Section */}
        <div className="relative bg-white/10 dark:bg-gray-900/40 backdrop-blur-lg rounded-3xl p-6 shadow-2xl border border-white/10 dark:border-gray-700 overflow-hidden">
          {/* Decorative shapes */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" />

          <div className="relative z-10 text-center">
            <h2 className="text-2xl font-extrabold text-white mb-2 drop-shadow">
              ¿Cómo querés tu pedido?
            </h2>
            <p className="text-gray-200 text-sm">
              Elegí si querés que te lo traigamos o si preferís retirarlo
            </p>
          </div>
        </div>

        {/* Order Type Options */}
        <div className="space-y-5">
          {/* Delivery Option */}
          {config?.accept_delivery && (
            <button
              onClick={() => handleSelect("delivery")}
              className="group w-full relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-md hover:bg-white dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-3xl p-6 shadow-xl flex items-center gap-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
            >
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} text-white group-hover:scale-110 transition-transform duration-300`}
              >
                <Bike className="w-8 h-8" />
              </div>

              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  Quiero que me lo traigan
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Te lo enviamos a tu domicilio
                </p>
              </div>

              <ArrowRight className="text-gray-400 group-hover:text-primary transition-colors w-6 h-6" />
            </button>
          )}

          {/* Pickup Option */}
          {config?.accept_takeaway && (
            <button
              onClick={() => handleSelect("pickup")}
              className="group w-full relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-md hover:bg-white dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-3xl p-6 shadow-xl flex items-center gap-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
            >
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} text-white group-hover:scale-110 transition-transform duration-300`}
              >
                <Package className="w-8 h-8" />
              </div>

              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  Voy a buscarlo
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Retirás tu pedido en el local
                </p>
              </div>

              <ArrowRight className="text-gray-400 group-hover:text-primary transition-colors w-6 h-6" />
            </button>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50/70 dark:bg-blue-900/20 border border-blue-200/40 dark:border-blue-800/40 rounded-2xl p-4 backdrop-blur-sm shadow-md">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow">
              <Info className="w-4 h-4" />
            </div>
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>Información:</strong> Si elegís <em>"Voy a buscarlo"</em>,
              te pediremos tu nombre para el retiro.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
